import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, RefreshCw, Globe, Monitor, Smartphone, Zap, 
  BarChart3, Archive, GitBranch, Cloud, Server, Database,
  AlertTriangle, CheckCircle, Clock, XCircle, Wrench, Box,
  Search, LayoutGrid, GitFork, X, Users, Link2, ArrowRight,
  ArrowLeftRight, Minimize2, Save, FolderOpen,
  Target, Trash2, Share2
} from 'lucide-react';
import { toast } from 'sonner';

// Icon components mapping
const TypeIcons = {
  'Web': Globe,
  'Desktop': Monitor,
  'Mobile': Smartphone,
  'API/Servicio': Zap,
  'ETL': RefreshCw,
  'BI/Reporting': BarChart3,
  'Legacy': Archive,
  'Middleware': GitBranch,
};

const TypeColors = {
  'Web': { bg: 'from-blue-500/20 to-blue-600/5', icon: 'text-blue-500', border: 'border-blue-400/60' },
  'Desktop': { bg: 'from-purple-500/20 to-purple-600/5', icon: 'text-purple-500', border: 'border-purple-400/60' },
  'Mobile': { bg: 'from-pink-500/20 to-pink-600/5', icon: 'text-pink-500', border: 'border-pink-400/60' },
  'API/Servicio': { bg: 'from-yellow-500/20 to-yellow-600/5', icon: 'text-yellow-600', border: 'border-yellow-400/60' },
  'ETL': { bg: 'from-cyan-500/20 to-cyan-600/5', icon: 'text-cyan-500', border: 'border-cyan-400/60' },
  'BI/Reporting': { bg: 'from-emerald-500/20 to-emerald-600/5', icon: 'text-emerald-500', border: 'border-emerald-400/60' },
  'Legacy': { bg: 'from-slate-500/20 to-slate-600/5', icon: 'text-slate-500', border: 'border-slate-400/60' },
  'Middleware': { bg: 'from-indigo-500/20 to-indigo-600/5', icon: 'text-indigo-500', border: 'border-indigo-400/60' },
};

const StatusConfig = {
  'En Producción': { color: '#22C55E', bgClass: 'bg-green-500', icon: CheckCircle, label: 'PROD' },
  'En Desarrollo': { color: '#3B82F6', bgClass: 'bg-blue-500', icon: Clock, label: 'DEV' },
  'En UAT': { color: '#F59E0B', bgClass: 'bg-amber-500', icon: AlertTriangle, label: 'UAT' },
  'Deprecada': { color: '#EF4444', bgClass: 'bg-red-500', icon: XCircle, label: 'DEP' },
  'Retirada': { color: '#6B7280', bgClass: 'bg-gray-500', icon: XCircle, label: 'OFF' },
  'En Mantenimiento': { color: '#8B5CF6', bgClass: 'bg-violet-500', icon: Wrench, label: 'MNT' },
};

// Custom node component - supports compact mode
const AppNode = ({ data, selected }) => {
  const TypeIcon = TypeIcons[data.type] || Box;
  const typeStyle = TypeColors[data.type] || TypeColors['Web'];
  const statusConfig = StatusConfig[data.status] || StatusConfig['En Producción'];
  const StatusIcon = statusConfig.icon;
  const isCompact = data.isCompact;
  
  // Compact mode: smaller, minimal info
  if (isCompact) {
    return (
      <div 
        className={`relative px-2 py-1.5 shadow-lg rounded-lg border bg-gradient-to-br ${typeStyle.bg} ${typeStyle.border} min-w-[120px] backdrop-blur-sm transition-all duration-200 hover:shadow-xl hover:scale-[1.02] ${selected ? 'ring-2 ring-primary' : ''}`}
      >
        <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-top-1" />
        <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-left-1" />
        
        <div className="flex items-center gap-1.5">
          <div className={`p-1 rounded bg-white/80 dark:bg-slate-800/80 ${typeStyle.icon}`}>
            <TypeIcon className="h-3 w-3" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[10px] text-foreground truncate max-w-[85px]" title={data.code}>{data.code}</div>
          </div>
          <div className={`w-2 h-2 rounded-full ${statusConfig.bgClass}`} title={data.status}></div>
        </div>
        
        <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-bottom-1" />
        <Handle type="source" position={Position.Right} className="!bg-slate-400 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-right-1" />
      </div>
    );
  }
  
  // Detailed mode (original)
  return (
    <div 
      className={`relative px-3 py-2 shadow-xl rounded-xl border-2 bg-gradient-to-br ${typeStyle.bg} ${typeStyle.border} min-w-[180px] backdrop-blur-sm transition-all duration-200 hover:shadow-2xl hover:scale-[1.02] ${selected ? 'ring-2 ring-primary' : ''}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-top-1" />
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-left-1" />
      
      <div className="flex items-start gap-2">
        <div className={`p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 shadow-sm ${typeStyle.icon}`}>
          <TypeIcon className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-xs text-foreground tracking-wide">{data.code}</div>
          <div className="text-[10px] text-muted-foreground truncate max-w-[110px]" title={data.label}>
            {data.label}
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/5 dark:border-white/10">
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-white text-[10px] font-semibold shadow-sm ${statusConfig.bgClass}`}>
          <StatusIcon className="h-3 w-3" strokeWidth={2} />
          {statusConfig.label}
        </div>
        {data.criticity && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${
            data.criticity === 'ALTA' ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' :
            data.criticity === 'MEDIA' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' :
            'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400'
          }`}>
            {data.criticity}
          </span>
        )}
      </div>
      
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-bottom-1" />
      <Handle type="source" position={Position.Right} className="!bg-slate-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-right-1" />
    </div>
  );
};

// Custom external system node - supports compact mode
const ExternalNode = ({ data, selected }) => {
  const isCompact = data.isCompact;
  const isUnresolved = data.isUnresolved;
  const NodeIcon = isUnresolved ? AlertTriangle : Cloud;
  const accentClasses = isUnresolved
    ? 'border-amber-400/70 bg-gradient-to-br from-amber-500/20 to-amber-600/5'
    : 'border-orange-400/70 bg-gradient-to-br from-orange-500/20 to-amber-500/5';
  const iconClasses = isUnresolved
    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
    : 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400';
  
  if (isCompact) {
    return (
      <div className={`relative px-2 py-1.5 shadow-lg rounded-lg border border-dashed ${accentClasses} min-w-[90px] backdrop-blur-sm transition-all duration-200 hover:shadow-xl hover:scale-[1.02] ${selected ? 'ring-2 ring-orange-500' : ''}`}>
        <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-top-1" />
        <Handle type="target" position={Position.Left} className="!bg-orange-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-left-1" />
        
        <div className="flex items-center gap-1.5">
          <div className={`p-1 rounded ${iconClasses}`}>
            <NodeIcon className="h-3 w-3" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] font-semibold text-foreground truncate max-w-[60px]" title={data.label}>{data.label}</div>
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-bottom-1" />
        <Handle type="source" position={Position.Right} className="!bg-orange-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-right-1" />
      </div>
    );
  }
  
  return (
    <div className={`relative px-3 py-2 shadow-xl rounded-xl border-2 border-dashed ${accentClasses} min-w-[150px] backdrop-blur-sm transition-all duration-200 hover:shadow-2xl hover:scale-[1.02] ${selected ? 'ring-2 ring-orange-500' : ''}`}>
      <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-top-1" />
      <Handle type="target" position={Position.Left} className="!bg-orange-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-left-1" />
      
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${iconClasses} shadow-sm`}>
          <NodeIcon className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-bold text-[9px] text-orange-600 dark:text-orange-400 uppercase tracking-widest">{isUnresolved ? 'Destino pendiente' : 'Externo'}</div>
          <div className="text-xs font-semibold text-foreground">{data.label}</div>
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-bottom-1" />
      <Handle type="source" position={Position.Right} className="!bg-orange-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-right-1" />
    </div>
  );
};

// Custom server node - supports compact mode
const ServerNode = ({ data, selected }) => {
  const isCompact = data.isCompact;
  
  if (isCompact) {
    return (
      <div className={`relative px-2 py-1.5 shadow-lg rounded-lg border border-teal-400/70 bg-gradient-to-br from-teal-500/20 to-teal-600/5 min-w-[90px] backdrop-blur-sm transition-all duration-200 hover:shadow-xl hover:scale-[1.02] ${selected ? 'ring-2 ring-teal-500' : ''}`}>
        <Handle type="target" position={Position.Top} className="!bg-teal-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-top-1" />
        <Handle type="target" position={Position.Left} className="!bg-teal-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-left-1" />
        
        <div className="flex items-center gap-1.5">
          <div className="p-1 rounded bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400">
            <Server className="h-3 w-3" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] font-semibold text-foreground truncate max-w-[60px]" title={data.hostname}>{data.hostname}</div>
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-teal-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-bottom-1" />
        <Handle type="source" position={Position.Right} className="!bg-teal-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-right-1" />
      </div>
    );
  }
  
  return (
    <div className={`relative px-3 py-2 shadow-xl rounded-xl border-2 border-teal-400/70 bg-gradient-to-br from-teal-500/20 to-teal-600/5 min-w-[140px] backdrop-blur-sm transition-all duration-200 hover:shadow-2xl hover:scale-[1.02] ${selected ? 'ring-2 ring-teal-500' : ''}`}>
      <Handle type="target" position={Position.Top} className="!bg-teal-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-top-1" />
      <Handle type="target" position={Position.Left} className="!bg-teal-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-left-1" />
      
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400 shadow-sm">
          <Server className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[9px] text-teal-600 dark:text-teal-400 uppercase tracking-widest">Servidor</div>
          <div className="text-xs font-semibold text-foreground truncate max-w-[90px]" title={data.hostname}>
            {data.hostname}
          </div>
          {data.ip && (
            <div className="text-[10px] text-muted-foreground font-mono">{data.ip}</div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-teal-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-bottom-1" />
      <Handle type="source" position={Position.Right} className="!bg-teal-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-right-1" />
    </div>
  );
};

// Custom database node - supports compact mode
const DatabaseNode = ({ data, selected }) => {
  const isCompact = data.isCompact;
  
  if (isCompact) {
    return (
      <div className={`relative px-2 py-1.5 shadow-lg rounded-lg border border-violet-400/70 bg-gradient-to-br from-violet-500/20 to-violet-600/5 min-w-[90px] backdrop-blur-sm transition-all duration-200 hover:shadow-xl hover:scale-[1.02] ${selected ? 'ring-2 ring-violet-500' : ''}`}>
        <Handle type="target" position={Position.Top} className="!bg-violet-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-top-1" />
        <Handle type="target" position={Position.Left} className="!bg-violet-500 !w-2 !h-2 !border !border-white dark:!border-slate-800 !-left-1" />
        
        <div className="flex items-center gap-1.5">
          <div className="p-1 rounded bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400">
            <Database className="h-3 w-3" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] font-semibold text-foreground truncate max-w-[60px]" title={data.db_name}>{data.db_name}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`relative px-3 py-2 shadow-xl rounded-xl border-2 border-violet-400/70 bg-gradient-to-br from-violet-500/20 to-violet-600/5 min-w-[130px] backdrop-blur-sm transition-all duration-200 hover:shadow-2xl hover:scale-[1.02] ${selected ? 'ring-2 ring-violet-500' : ''}`}>
      <Handle type="target" position={Position.Top} className="!bg-violet-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-top-1" />
      <Handle type="target" position={Position.Left} className="!bg-violet-500 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-slate-800 !-left-1" />
      
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 shadow-sm">
          <Database className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[9px] text-violet-600 dark:text-violet-400 uppercase tracking-widest">{data.db_type || 'BD'}</div>
          <div className="text-xs font-semibold text-foreground truncate max-w-[85px]" title={data.db_name}>
            {data.db_name}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-violet-500/20">
        {data.is_primary && (
          <span className="text-[8px] px-1 py-0.5 rounded bg-violet-500/20 text-violet-600 dark:text-violet-400 font-semibold">PRIMARY</span>
        )}
        <span className="text-[9px] text-muted-foreground">{data.access_type}</span>
      </div>
    </div>
  );
};

const nodeTypes = {
  app: AppNode,
  external: ExternalNode,
  server: ServerNode,
  database: DatabaseNode,
};

const getDirectionDetails = (direction) => {
  const normalizedDirection = direction?.trim().toLowerCase();

  if (normalizedDirection === 'in' || normalizedDirection === 'inbound') {
    return { Icon: ArrowLeft, label: 'Entrada' };
  }
  if (normalizedDirection === 'out' || normalizedDirection === 'outbound') {
    return { Icon: ArrowRight, label: 'Salida' };
  }
  if (normalizedDirection === 'bidirectional' || normalizedDirection === 'bidireccional') {
    return { Icon: ArrowLeftRight, label: 'Bidireccional' };
  }

  return { Icon: ArrowLeftRight, label: direction || 'Sin definir' };
};

const getInterfaceEdgeVisuals = (criticity, isExternal = false) => {
  const color = criticity === 'ALTA' ? '#EF4444' :
    criticity === 'MEDIA' ? '#F59E0B' : '#22C55E';

  return {
    color,
    animated: criticity === 'ALTA',
    style: {
      stroke: color,
      strokeWidth: criticity === 'ALTA' ? 3 : 2,
      ...(isExternal ? { strokeDasharray: '8,4' } : {}),
    },
  };
};

const getEnvironmentSuffix = (environment) => (
  environment && environment !== 'Producción' ? ` · ${environment}` : ''
);

const getAppServerLabel = (role, environment) => {
  const normalizedRole = role?.trim().toLowerCase();
  const action = normalizedRole === 'base de datos' || normalizedRole === 'bd'
    ? 'Usa BD'
    : role ? `Ejecuta · ${role}` : 'Ejecuta';
  return `${action}${getEnvironmentSuffix(environment)}`;
};

const getAppDatabaseLabel = (environment) => `Usa${getEnvironmentSuffix(environment)}`;

// Tooltip Component
const Tooltip = ({ data, position, type }) => {
  if (!data || !position) return null;
  const directionDetails = getDirectionDetails(data.direction);
  const DirectionIcon = directionDetails.Icon;
  const isInfrastructureEdge = ['app-server', 'app-database', 'database-server'].includes(data.type);
  
  return (
    <div 
      className="fixed z-50 pointer-events-none"
      style={{ left: position.x + 15, top: position.y + 15 }}
    >
      <div className="bg-slate-900/95 text-white text-xs rounded-lg shadow-2xl border border-slate-700 p-3 max-w-[280px] backdrop-blur-sm">
        {type === 'node' && (
          <>
            <div className="font-bold text-sm text-primary mb-1">{data.code}</div>
            <div className="text-slate-300 mb-2">{data.label}</div>
            {data.description && (
              <div className="text-slate-400 text-[11px] mb-2 italic">{data.description}</div>
            )}
            <div className="space-y-1.5 pt-2 border-t border-slate-700">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Tipo:</span>
                <span className="text-slate-200">{data.type || 'Sin definir'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Estado:</span>
                <span className="text-slate-200">{data.status || 'Sin definir'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Criticidad:</span>
                <span className={`font-medium ${
                  data.criticity === 'ALTA' ? 'text-red-400' :
                  data.criticity === 'MEDIA' ? 'text-amber-400' : 'text-green-400'
                }`}>{data.criticity || 'Sin definir'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Server className="h-3 w-3 text-slate-400" />
                <span className="text-slate-400">Servidores:</span>
                <span className="text-slate-200">{data.servers_count || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Link2 className="h-3 w-3 text-slate-400" />
                <span className="text-slate-400">Interfaces:</span>
                <span className="text-slate-200">{data.interfaces_count || 0}</span>
              </div>
              {data.owners_count > 0 && (
                <div className="flex items-start gap-2">
                  <Users className="h-3 w-3 text-slate-400 mt-0.5" />
                  <div>
                    <span className="text-slate-400">Responsables:</span>
                    <div className="text-slate-300 text-[10px] mt-0.5">{data.owners_preview}</div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {type === 'edge' && (
          <>
            <div className="font-bold text-sm text-primary mb-1">{data.name || data.action || 'Interfaz'}</div>
            <div className="space-y-1.5">
              {isInfrastructureEdge ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Relación:</span>
                    <span className="text-cyan-400 font-medium">{data.action || 'Infraestructura'}</span>
                  </div>
                  {data.role && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Rol:</span>
                      <span className="text-slate-200">{data.role}</span>
                    </div>
                  )}
                  {data.environment && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Ambiente:</span>
                      <span className="text-slate-200">{data.environment}</span>
                    </div>
                  )}
                  {data.database_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Base:</span>
                      <span className="text-slate-200">{data.database_name}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Tipo:</span>
                    <span className="text-cyan-400 font-medium">{data.interface_type || 'Sin definir'}</span>
                  </div>
                  {data.direction && (
                    <div className="flex items-center gap-2">
                      <DirectionIcon className="h-3 w-3 text-slate-400" />
                      <span className="text-slate-400">Dirección:</span>
                      <span className="text-slate-200">{directionDetails.label}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Criticidad:</span>
                    <span className={`font-medium ${
                      data.criticity === 'ALTA' ? 'text-red-400' :
                      data.criticity === 'MEDIA' ? 'text-amber-400' : 'text-green-400'
                    }`}>{data.criticity || 'BAJA'}</span>
                  </div>
                  {(data.visual_source_code || data.source_code) && (data.visual_target_code || data.target_code || data.external_system_name) && (
                    <div className="pt-2 border-t border-slate-700 mt-2">
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="text-blue-400">{data.visual_source_code || data.source_code}</span>
                        <ArrowRight className="h-3 w-3 text-slate-500" />
                        <span className="text-green-400">{data.visual_target_code || data.target_code || data.external_system_name}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              {data.notes && (
                <div className="pt-2 border-t border-slate-700 mt-2">
                  <div className="text-slate-400 text-[10px]">Notas:</div>
                  <div className="text-slate-300 text-[11px] italic">{data.notes}</div>
                </div>
              )}
            </div>
          </>
        )}
        {type === 'external' && (
          <>
            <div className="font-bold text-sm text-orange-400 mb-1">{data.label}</div>
            <div className="text-slate-400">Sistema externo</div>
            <div className="text-slate-500 text-[10px] mt-1">Click para más información no disponible</div>
          </>
        )}
        {type === 'server' && (
          <>
            <div className="font-bold text-sm text-teal-400 mb-1">{data.hostname}</div>
            {data.ip && <div className="text-slate-300 font-mono text-[11px] mb-2">{data.ip}</div>}
            <div className="space-y-1.5 pt-2 border-t border-slate-700">
              {data.os && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">SO:</span>
                  <span className="text-slate-200">{data.os}</span>
                </div>
              )}
              {data.location && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Ubicación:</span>
                  <span className="text-slate-200">{data.location}</span>
                </div>
              )}
              {data.description && (
                <div className="text-slate-400 text-[10px] italic mt-1">{data.description}</div>
              )}
            </div>
          </>
        )}
        {type === 'database' && (
          <>
            <div className="font-bold text-sm text-violet-400 mb-1">{data.db_name}</div>
            <div className="text-slate-300 text-[11px] mb-2">{data.db_type}</div>
            <div className="space-y-1.5 pt-2 border-t border-slate-700">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Ambiente:</span>
                <span className="text-slate-200">{data.environment || '-'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Acceso:</span>
                <span className="text-slate-200">{data.access_type || '-'}</span>
              </div>
              {data.is_primary && (
                <div className="text-violet-400 text-[10px] font-semibold">Base de datos primaria</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Dagre layout helper - supports compact mode and LR orientation
const getLayoutedElements = (nodes, edges, direction = 'LR', isCompact = false) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Dagre needs a conservative footprint. The rendered nodes can grow with
  // long hostnames or labels, so reserving the smallest per-type size causes
  // visual overlaps even when the computed coordinates do not intersect.
  const nodeDimensions = isCompact
    ? { width: 140, height: 60 }
    : { width: 210, height: 110 };
  
  const nodesep = isCompact ? 45 : 85;
  const ranksep = isCompact ? 80 : 120;
  
  dagreGraph.setGraph({ 
    rankdir: direction,
    nodesep: nodesep,
    ranksep: ranksep,
    marginx: 30,
    marginy: 30,
    ranker: 'network-simplex',
  });

  nodes.forEach((node) => {
    // Dagre mutates node labels with computed coordinates. Each node needs
    // its own dimensions object so those coordinates are never shared.
    dagreGraph.setNode(node.id, { ...nodeDimensions });
  });

  [...edges].sort((first, second) => (
    `${first.source}-${first.target}-${first.id}`.localeCompare(`${second.source}-${second.target}-${second.id}`)
  )).forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const { width, height } = nodeDimensions;
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Grid layout helper - supports compact mode
const getGridLayout = (nodes, cols = null, isCompact = false) => {
  const numCols = cols || Math.ceil(Math.sqrt(nodes.length));
  const spacing = isCompact ? { x: 180, y: 70 } : { x: 260, y: 140 };
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: (index % numCols) * spacing.x + 30,
      y: Math.floor(index / numCols) * spacing.y + 30,
    },
  }));
};

const ApplicationMap = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canCreate, canUpdate, canDelete } = usePermissions('apps', 'map');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  
  // React Flow instance for viewport control
  const reactFlowInstance = useReactFlow();
  
  // Get focus from URL params
  const urlFocusApp = searchParams.get('focus');
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCriticity, setFilterCriticity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterServer, setFilterServer] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [focusAppId, setFocusAppId] = useState(urlFocusApp || 'all'); // "Centrar en App"
  
  // Layout
  const [layoutType, setLayoutType] = useState('grid');
  const [isCompact, setIsCompact] = useState(false);
  
  // Tooltip state
  const [tooltip, setTooltip] = useState({ data: null, position: null, type: null });
  
  // Interface modal state
  const [interfaceModal, setInterfaceModal] = useState({ open: false, data: null });
  
  // Raw data
  const [rawData, setRawData] = useState({ nodes: [], edges: [] });
  const [servers, setServers] = useState([]);
  const [applications, setApplications] = useState([]); // For "Focus on App" dropdown
  
  // Saved Maps state
  const [savedMaps, setSavedMaps] = useState([]);
  const [currentMapId, setCurrentMapId] = useState(null);
  const [saveMapModal, setSaveMapModal] = useState({ open: false, mode: 'create', data: null });
  const [customPositions, setCustomPositions] = useState({}); // Track user-dragged positions
  
  // Store base nodes/edges and edge data for tooltips
  const baseEdgesRef = useRef([]);
  const edgeDataMapRef = useRef(new Map());

  const fetchMapData = async () => {
    setLoading(true);
    try {
      const [mapResponse, savedMapsResponse] = await Promise.all([
        api.get('/assets/applications/map/data'),
        api.get('/assets/applications/saved-maps'),
      ]);
      setRawData(mapResponse.data);
      setServers(mapResponse.data.servers || []);
      setApplications(mapResponse.data.nodes || []);
      setSavedMaps(savedMapsResponse.data || []);
    } catch (error) {
      toast.error('Error al cargar datos del mapa');
    } finally {
      setLoading(false);
    }
  };

  // Get connected node IDs for hover highlight
  const getConnectedNodeIds = useCallback((nodeId, edgeList) => {
    const connected = new Set([nodeId]);
    edgeList.forEach(edge => {
      if (edge.source === nodeId) connected.add(edge.target);
      if (edge.target === nodeId) connected.add(edge.source);
    });
    return connected;
  }, []);

  // Process map data
  const processMapData = useCallback((data, filters, layout, compactMode, savedPositions = {}) => {
    const { status, criticity, type, server, search, focusApp } = filters;
    
    let filteredNodes = data.nodes || [];
    
    // "Centrar en App" filter - show only the focused app and its direct connections
    if (focusApp && focusApp !== 'all') {
      const focusAppIdNum = parseInt(focusApp);
      const edgeData = data.edges || [];
      const appServers = data.app_servers || [];
      const appDatabases = data.app_databases || [];
      
      // Get connected app IDs (apps connected via interfaces)
      const connectedAppIds = new Set([focusAppIdNum]);
      edgeData.forEach(edge => {
        if (edge.source_app_id === focusAppIdNum && edge.target_app_id) {
          connectedAppIds.add(edge.target_app_id);
        }
        if (edge.target_app_id === focusAppIdNum) {
          connectedAppIds.add(edge.source_app_id);
        }
      });
      
      filteredNodes = filteredNodes.filter(n => connectedAppIds.has(n.id));
    }
    
    if (status !== 'all') {
      filteredNodes = filteredNodes.filter(n => n.status === status);
    }
    if (criticity !== 'all') {
      filteredNodes = filteredNodes.filter(n => n.criticity === criticity);
    }
    if (type !== 'all') {
      filteredNodes = filteredNodes.filter(n => n.type === type);
    }
    if (server !== 'all') {
      const serverIdNum = parseInt(server);
      filteredNodes = filteredNodes.filter(n => 
        n.server_ids && n.server_ids.includes(serverIdNum)
      );
    }
    if (search && search.trim().length >= 2) {
      const searchLower = search.toLowerCase();
      filteredNodes = filteredNodes.filter(n => 
        n.code?.toLowerCase().includes(searchLower) ||
        n.name?.toLowerCase().includes(searchLower)
      );
    }

    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const externalSystems = new Map();
    const flowEdges = [];
    const edgeData = data.edges || [];
    
    // Build edge data map for tooltips
    const newEdgeDataMap = new Map();
    
    edgeData.forEach((edge) => {
      const sourceInFilter = nodeIds.has(edge.source_app_id);
      const normalizedDirection = edge.direction?.trim().toLowerCase();
      const isInbound = normalizedDirection === 'in' || normalizedDirection === 'inbound';
      const isBidirectional = normalizedDirection === 'bidirectional' || normalizedDirection === 'bidireccional';
      const interfaceLabel = edge.name || edge.interface_type || 'Integra';
      const visuals = getInterfaceEdgeVisuals(edge.criticity);
      
      if (sourceInFilter) {
        if (edge.target_app_id && nodeIds.has(edge.target_app_id)) {
          const edgeId = `edge-${edge.id}`;
          newEdgeDataMap.set(edgeId, {
            ...edge,
            visual_source_code: isInbound ? edge.target_code : edge.source_code,
            visual_target_code: isInbound ? edge.source_code : edge.target_code,
          });
          
          flowEdges.push({
            id: edgeId,
            source: `app-${isInbound ? edge.target_app_id : edge.source_app_id}`,
            target: `app-${isInbound ? edge.source_app_id : edge.target_app_id}`,
            label: interfaceLabel,
            labelStyle: { fontSize: 10, fill: '#666', fontWeight: 500 },
            labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
            labelBgPadding: [6, 4],
            labelBgBorderRadius: 4,
            type: 'smoothstep',
            animated: visuals.animated,
            style: visuals.style,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: visuals.color,
              width: 20,
              height: 20,
            },
            ...(isBidirectional ? {
              markerStart: { type: MarkerType.ArrowClosed, color: visuals.color, width: 20, height: 20 },
            } : {}),
          });
        } else if (edge.external_system_name?.trim()) {
          const extKey = edge.external_system_name.trim();
          if (!externalSystems.has(extKey)) {
            externalSystems.set(extKey, {
              id: `ext-${extKey.replace(/\s+/g, '-').toLowerCase()}`,
              name: extKey,
            });
          }
          
          const edgeId = `edge-${edge.id}`;
          const externalVisuals = getInterfaceEdgeVisuals(edge.criticity, true);
          newEdgeDataMap.set(edgeId, {
            ...edge,
            visual_source_code: isInbound ? extKey : edge.source_code,
            visual_target_code: isInbound ? edge.source_code : extKey,
          });
          
          flowEdges.push({
            id: edgeId,
            source: isInbound ? externalSystems.get(extKey).id : `app-${edge.source_app_id}`,
            target: isInbound ? `app-${edge.source_app_id}` : externalSystems.get(extKey).id,
            label: interfaceLabel,
            labelStyle: { fontSize: 10, fill: '#666', fontWeight: 500 },
            labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
            labelBgPadding: [6, 4],
            labelBgBorderRadius: 4,
            type: 'smoothstep',
            animated: externalVisuals.animated,
            style: externalVisuals.style,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: externalVisuals.color,
              width: 20,
              height: 20,
            },
            ...(isBidirectional ? {
              markerStart: { type: MarkerType.ArrowClosed, color: externalVisuals.color, width: 20, height: 20 },
            } : {}),
          });
        } else {
          const unresolvedKey = `unresolved-${edge.id}`;
          externalSystems.set(unresolvedKey, {
            id: `ext-${unresolvedKey}`,
            name: edge.name || 'Interfaz sin nombre',
            isUnresolved: true,
          });

          const edgeId = `edge-${edge.id}`;
          newEdgeDataMap.set(edgeId, edge);
          flowEdges.push({
            id: edgeId,
            source: `app-${edge.source_app_id}`,
            target: `ext-${unresolvedKey}`,
            label: edge.interface_type || edge.name,
            labelStyle: { fontSize: 10, fill: '#b45309', fontWeight: 500 },
            labelBgStyle: { fill: '#fffbeb', fillOpacity: 0.95 },
            labelBgPadding: [6, 4],
            labelBgBorderRadius: 4,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#F59E0B', strokeWidth: 2, strokeDasharray: '6,3' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#F59E0B', width: 20, height: 20 },
          });
        }
      }
    });
    
    edgeDataMapRef.current = newEdgeDataMap;

    // Create flow nodes with full data for tooltips
    let flowNodes = filteredNodes.map((node) => ({
      id: `app-${node.id}`,
      type: 'app',
      position: { x: 0, y: 0 },
      data: {
        code: node.code,
        label: node.name,
        description: node.description,
        type: node.type,
        status: node.status,
        criticity: node.criticity,
        servers_count: node.servers_count,
        interfaces_count: node.interfaces_count,
        owners_count: node.owners_count,
        owners_preview: node.owners_preview,
        isCompact: compactMode,
      },
    }));

    const extNodes = Array.from(externalSystems.values()).map((ext) => ({
      id: ext.id,
      type: 'external',
      position: { x: 0, y: 0 },
      data: {
        label: ext.name,
        isUnresolved: ext.isUnresolved,
        isCompact: compactMode,
      },
    }));

    // Create server nodes from app_servers relationships
    const serverMap = new Map();
    const serverData = data.servers || [];
    const appServers = data.app_servers || [];
    
    // Build server lookup
    serverData.forEach(srv => {
      serverMap.set(srv.id, srv);
    });
    
    const serverNodesMap = new Map();
    const ensureServerNode = (serverId) => {
      const server = serverMap.get(serverId);
      if (!server) return false;

      if (!serverNodesMap.has(server.id)) {
        serverNodesMap.set(server.id, {
          id: `server-${server.id}`,
          type: 'server',
          position: { x: 0, y: 0 },
          data: {
            hostname: server.hostname,
            ip: server.ip_address,
            os: server.os_name,
            location: server.location_name,
            description: server.description,
            isCompact: compactMode,
          },
        });
      }

      return true;
    };

    // Tracks each visual APP → SERVER link so a database-host relationship
    // does not duplicate an explicit application-server relationship.
    const appServerEdgeKeys = new Set();

    // Create server nodes only for servers linked to filtered apps.
    appServers.forEach(rel => {
      const environment = rel.environment || 'Producción';
      const appServerKey = `${rel.application_id}-${rel.server_id}-${environment}`;
      if (nodeIds.has(rel.application_id) && ensureServerNode(rel.server_id) && !appServerEdgeKeys.has(appServerKey)) {
        appServerEdgeKeys.add(appServerKey);
        const relationLabel = getAppServerLabel(rel.role, environment);
        
        // Create edge from app to server
        const serverEdgeId = `app-server-${rel.id}`;
        flowEdges.push({
          id: serverEdgeId,
          source: `app-${rel.application_id}`,
          target: `server-${rel.server_id}`,
          label: relationLabel,
          labelStyle: { fontSize: 9, fill: '#14b8a6', fontWeight: 500 },
          labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
          labelBgPadding: [4, 3],
          labelBgBorderRadius: 3,
          type: 'smoothstep',
          animated: false,
          style: { 
            stroke: '#14b8a6',
            strokeWidth: 2,
            strokeDasharray: '5,5',
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#14b8a6',
            width: 16,
            height: 16,
          },
        });
        
        // Store edge data for tooltip
        newEdgeDataMap.set(serverEdgeId, {
          name: `${rel.app_code} → ${rel.server_hostname}`,
          type: 'app-server',
          environment: rel.environment,
          role: rel.role,
          action: relationLabel,
          notes: rel.notes,
          app_code: rel.app_code,
          server_hostname: rel.server_hostname,
        });
      }
    });
    
    // Create database nodes from app_databases relationships
    const databaseNodesMap = new Map();
    const appDatabases = data.app_databases || [];
    const hostingEdgeIds = new Set();
    
    // Create database nodes only for databases linked to filtered apps
    appDatabases.forEach(rel => {
      if (nodeIds.has(rel.application_id)) {
        const databaseNodeId = rel.sql_database_id ? `db-sql-${rel.sql_database_id}` : `db-${rel.id}`;
        if (!databaseNodesMap.has(databaseNodeId)) {
          databaseNodesMap.set(databaseNodeId, {
            id: databaseNodeId,
            type: 'database',
            position: { x: 0, y: 0 },
            data: {
              db_name: rel.db_name,
              db_type: rel.db_type,
              environment: rel.environment,
              is_primary: rel.is_primary,
              access_type: rel.access_type,
              isCompact: compactMode,
            },
          });
        }
        
        const hasHostServer = rel.host_server_id && ensureServerNode(rel.host_server_id);

        if (hasHostServer) {
          const environment = rel.environment || 'Producción';
          const appServerKey = `${rel.application_id}-${rel.host_server_id}-${environment}`;
          const hostServer = serverMap.get(rel.host_server_id);
          const relationLabel = getAppServerLabel('Base de datos', environment);

          // Existing relations are rendered as the same chain as new ones.
          // New database links persist this relation in udt_app_servers; old
          // links are inferred here without mutating historical data on GET.
          if (!appServerEdgeKeys.has(appServerKey)) {
            appServerEdgeKeys.add(appServerKey);
            const appServerEdgeId = `app-server-db-host-${rel.id}`;
            flowEdges.push({
              id: appServerEdgeId,
              source: `app-${rel.application_id}`,
              target: `server-${rel.host_server_id}`,
              label: relationLabel,
              labelStyle: { fontSize: 9, fill: '#14b8a6', fontWeight: 500 },
              labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
              labelBgPadding: [4, 3],
              labelBgBorderRadius: 3,
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#14b8a6', strokeWidth: 2, strokeDasharray: '5,5' },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#14b8a6', width: 16, height: 16 },
            });
            newEdgeDataMap.set(appServerEdgeId, {
              name: `${rel.app_code} → ${hostServer.hostname}`,
              type: 'app-server',
              environment,
              role: 'Base de datos',
              action: relationLabel,
              notes: `Derivado de la base de datos ${rel.db_name}`,
              app_code: rel.app_code,
              server_hostname: hostServer.hostname,
            });
          }

          const hostingEdgeId = `server-db-${rel.host_server_id}-${databaseNodeId}`;
          if (!hostingEdgeIds.has(hostingEdgeId)) {
            hostingEdgeIds.add(hostingEdgeId);
            flowEdges.push({
              id: hostingEdgeId,
              source: `server-${rel.host_server_id}`,
              target: databaseNodeId,
              label: 'Aloja',
              labelStyle: { fontSize: 9, fill: '#0ea5e9', fontWeight: 500 },
              labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
              labelBgPadding: [4, 3],
              labelBgBorderRadius: 3,
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#0ea5e9', strokeWidth: 2, strokeDasharray: '3,3' },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#0ea5e9', width: 16, height: 16 },
            });
            newEdgeDataMap.set(hostingEdgeId, {
              name: `${hostServer.hostname} aloja ${rel.db_name}`,
              type: 'database-server',
              action: 'Aloja',
              environment: rel.environment,
              database_name: rel.db_name,
            });
          }
        } else {
          // Manual databases, or records whose host cannot be resolved, retain
          // their direct application relationship so no dependency is hidden.
          const dbEdgeId = `app-db-${rel.id}`;
          flowEdges.push({
            id: dbEdgeId,
            source: `app-${rel.application_id}`,
            target: databaseNodeId,
            label: getAppDatabaseLabel(rel.environment),
            labelStyle: { fontSize: 9, fill: '#8b5cf6', fontWeight: 500 },
            labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.95 },
            labelBgPadding: [4, 3],
            labelBgBorderRadius: 3,
            type: 'smoothstep',
            animated: false,
            style: {
              stroke: '#8b5cf6',
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#8b5cf6',
              width: 16,
              height: 16,
            },
          });

          newEdgeDataMap.set(dbEdgeId, {
            name: `${rel.app_code} → ${rel.db_name}`,
            type: 'app-database',
            action: getAppDatabaseLabel(rel.environment),
            db_type: rel.db_type,
            environment: rel.environment,
            access_type: rel.access_type,
            is_primary: rel.is_primary,
            app_code: rel.app_code,
            db_name: rel.db_name,
          });
        }
      }
    });
    
    const serverNodes = Array.from(serverNodesMap.values());
    const databaseNodes = Array.from(databaseNodesMap.values());

    let allNodes = [...flowNodes, ...extNodes, ...serverNodes, ...databaseNodes];

    // Apply layout
    if (layout === 'hierarchy' && allNodes.length > 0) {
      const layouted = getLayoutedElements(allNodes, flowEdges, 'LR', compactMode);
      allNodes = layouted.nodes;
    } else {
      allNodes = getGridLayout(allNodes, null, compactMode);
    }
    
    // Apply saved/custom positions if available
    if (Object.keys(savedPositions).length > 0) {
      allNodes = allNodes.map(node => {
        if (savedPositions[node.id]) {
          return { ...node, position: savedPositions[node.id] };
        }
        return node;
      });
    }

    baseEdgesRef.current = flowEdges;
    setNodes(allNodes);
    setEdges(flowEdges);
  }, [setNodes, setEdges]);

  useEffect(() => {
    fetchMapData();
  }, []);

  useEffect(() => {
    if (rawData.nodes?.length > 0) {
      processMapData(
        rawData, 
        { 
          status: filterStatus, 
          criticity: filterCriticity, 
          type: filterType, 
          server: filterServer, 
          search: searchText,
          focusApp: focusAppId 
        },
        layoutType,
        isCompact,
        customPositions
      );
    }
  }, [filterStatus, filterCriticity, filterType, filterServer, searchText, focusAppId, rawData, layoutType, isCompact, customPositions, processMapData]);

  // Handle node drag end - save position
  const onNodeDragStop = useCallback((event, node) => {
    setCustomPositions(prev => ({
      ...prev,
      [node.id]: node.position
    }));
  }, []);

  const onNodeClick = useCallback((event, node) => {
    if (node.id.startsWith('app-')) {
      const appId = node.id.replace('app-', '');
      navigate(`/apps/${appId}`);
    }
  }, [navigate]);

  // Node hover handlers
  const onNodeMouseEnter = useCallback((event, node) => {
    const connectedIds = getConnectedNodeIds(node.id, baseEdgesRef.current);
    
    setNodes((nds) => nds.map((n) => ({
      ...n,
      className: connectedIds.has(n.id) ? 'highlighted-node' : 'dimmed-node',
    })));
    
    setEdges((eds) => eds.map((e) => {
      const isConnected = connectedIds.has(e.source) && connectedIds.has(e.target);
      return {
        ...e,
        style: { ...e.style, opacity: isConnected ? 1 : 0.15 },
        labelStyle: { ...e.labelStyle, opacity: isConnected ? 1 : 0.15 },
      };
    }));
    
    // Show tooltip - detect node type
    let tooltipType = 'node';
    if (node.type === 'external') tooltipType = 'external';
    else if (node.type === 'server') tooltipType = 'server';
    else if (node.type === 'database') tooltipType = 'database';
    
    setTooltip({
      data: node.data,
      position: { x: event.clientX, y: event.clientY },
      type: tooltipType,
    });
  }, [setNodes, setEdges, getConnectedNodeIds]);

  const onNodeMouseMove = useCallback((event) => {
    if (tooltip.data) {
      setTooltip(prev => ({
        ...prev,
        position: { x: event.clientX, y: event.clientY },
      }));
    }
  }, [tooltip.data]);

  const onNodeMouseLeave = useCallback(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, className: '' })));
    setEdges((eds) => eds.map((e) => ({
      ...e,
      style: { ...e.style, opacity: 1 },
      labelStyle: { ...e.labelStyle, opacity: 1 },
    })));
    setTooltip({ data: null, position: null, type: null });
  }, [setNodes, setEdges]);

  // Edge hover handlers
  const onEdgeMouseEnter = useCallback((event, edge) => {
    const edgeData = edgeDataMapRef.current.get(edge.id);
    if (edgeData) {
      setTooltip({
        data: edgeData,
        position: { x: event.clientX, y: event.clientY },
        type: 'edge',
      });
    }
  }, []);

  const onEdgeMouseMove = useCallback((event) => {
    if (tooltip.type === 'edge') {
      setTooltip(prev => ({
        ...prev,
        position: { x: event.clientX, y: event.clientY },
      }));
    }
  }, [tooltip.type]);

  const onEdgeMouseLeave = useCallback(() => {
    if (tooltip.type === 'edge') {
      setTooltip({ data: null, position: null, type: null });
    }
  }, [tooltip.type]);

  // Edge click handler - open interface modal
  const onEdgeClick = useCallback((event, edge) => {
    const edgeData = edgeDataMapRef.current.get(edge.id);
    if (edgeData) {
      setTooltip({ data: null, position: null, type: null }); // Close tooltip
      setInterfaceModal({ open: true, data: edgeData });
    }
  }, []);

  // Get unique values for filters
  const types = useMemo(() => {
    return [...new Set(rawData.nodes?.map(n => n.type).filter(Boolean))].sort();
  }, [rawData.nodes]);

  const statuses = useMemo(() => {
    return [...new Set(rawData.nodes?.map(n => n.status).filter(Boolean))];
  }, [rawData.nodes]);

  const criticities = useMemo(() => {
    return [...new Set(rawData.nodes?.map(n => n.criticity).filter(Boolean))];
  }, [rawData.nodes]);

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterCriticity('all');
    setFilterType('all');
    setFilterServer('all');
    setSearchText('');
    setFocusAppId('all');
    setCurrentMapId(null);
    setCustomPositions({});
  };

  const applyAutomaticLayout = (nextLayout) => {
    // A deliberate layout change must recalculate from the graph topology,
    // rather than restoring a position created by dragging or a saved map.
    setCustomPositions({});
    setLayoutType(nextLayout);
  };

  const hasActiveFilters = filterStatus !== 'all' || filterCriticity !== 'all' || 
                           filterType !== 'all' || filterServer !== 'all' || 
                           searchText.trim().length > 0 || focusAppId !== 'all';

  const currentMap = useMemo(
    () => savedMaps.find(map => map.id === currentMapId),
    [savedMaps, currentMapId]
  );
  const isCurrentMapOwner = Boolean(
    currentMap && Number(currentMap.created_by) === Number(user?.id)
  );

  // Load a saved map
  const loadSavedMap = async (mapId) => {
    if (!mapId || mapId === 'full') {
      clearFilters();
      return;
    }
    
    try {
      const response = await api.get(`/assets/applications/saved-maps/${mapId}`);
      const mapData = response.data;
      
      setCurrentMapId(mapId);
      
      // Apply filter config
      if (mapData.filter_config) {
        const fc = mapData.filter_config;
        if (fc.focusAppId) setFocusAppId(String(fc.focusAppId));
        if (fc.status) setFilterStatus(fc.status);
        if (fc.criticity) setFilterCriticity(fc.criticity);
        if (fc.type) setFilterType(fc.type);
        if (fc.server) setFilterServer(fc.server);
        if (fc.layoutType) setLayoutType(fc.layoutType);
        if (fc.isCompact !== undefined) setIsCompact(fc.isCompact);
        
        // Apply viewport (zoom + pan) with a small delay to ensure nodes are rendered
        if (fc.viewport) {
          setTimeout(() => {
            reactFlowInstance.setViewport({
              x: fc.viewport.x,
              y: fc.viewport.y,
              zoom: fc.viewport.zoom
            }, { duration: 300 });
          }, 100);
        }
      }
      
      // Apply layout positions
      if (mapData.layout_positions) {
        setCustomPositions(mapData.layout_positions);
      }
      
      toast.success(`Mapa "${mapData.name}" cargado`);
    } catch (error) {
      toast.error('Error al cargar el mapa');
    }
  };

  // Save current view as map
  const saveMap = async (name, description, isShared, mapId = null) => {
    // Get current viewport (zoom + pan position)
    const viewport = reactFlowInstance.getViewport();
    
    const filterConfig = {
      focusAppId: focusAppId !== 'all' ? parseInt(focusAppId) : null,
      status: filterStatus,
      criticity: filterCriticity,
      type: filterType,
      server: filterServer,
      layoutType,
      isCompact,
      viewport: { x: viewport.x, y: viewport.y, zoom: viewport.zoom },
    };
    
    // Get current node positions from the React Flow state
    const layoutPositions = {};
    nodes.forEach(node => {
      layoutPositions[node.id] = node.position;
    });
    
    try {
      if (mapId) {
        // Update existing
        await api.put(`/assets/applications/saved-maps/${mapId}`, {
          name,
          description,
          filter_config: filterConfig,
          layout_positions: layoutPositions,
          is_shared: isShared,
        });
        toast.success('Mapa actualizado');
      } else {
        // Create new
        const response = await api.post('/assets/applications/saved-maps', {
          name,
          description,
          filter_config: filterConfig,
          layout_positions: layoutPositions,
          is_shared: isShared,
        });
        setCurrentMapId(response.data.id);
        toast.success('Mapa guardado');
      }
      
      // Refresh saved maps list
      const mapsResponse = await api.get('/assets/applications/saved-maps');
      setSavedMaps(mapsResponse.data || []);
      setSaveMapModal({ open: false, mode: 'create', data: null });
    } catch (error) {
      toast.error('Error al guardar el mapa');
    }
  };

  // Delete a saved map
  const deleteMap = async (mapId) => {
    try {
      await api.delete(`/assets/applications/saved-maps/${mapId}`);
      toast.success('Mapa eliminado');
      
      if (currentMapId === mapId) {
        clearFilters();
      }
      
      // Refresh list
      const mapsResponse = await api.get('/assets/applications/saved-maps');
      setSavedMaps(mapsResponse.data || []);
    } catch (error) {
      toast.error('Error al eliminar el mapa');
    }
  };

  const interfaceDirection = getDirectionDetails(interfaceModal.data?.direction);
  const InterfaceDirectionIcon = interfaceDirection.Icon;
  const displayedInterfaceCount = edges.filter(edge => edge.id.startsWith('edge-')).length;

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)] flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/apps')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Mapa de Aplicaciones</h1>
              <p className="text-muted-foreground">
                {nodes.filter(n => n.type === 'app').length} aplicaciones, {displayedInterfaceCount} interfaces
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 bg-muted/50 p-1 rounded-lg">
            <Button 
              variant={layoutType === 'grid' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => applyAutomaticLayout('grid')}
              className="gap-2"
              data-testid="layout-grid-btn"
            >
              <LayoutGrid className="h-4 w-4" />
              Grid
            </Button>
            <Button 
              variant={layoutType === 'hierarchy' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => applyAutomaticLayout('hierarchy')}
              className="gap-2"
              data-testid="layout-hierarchy-btn"
            >
              <GitFork className="h-4 w-4" />
              Jerarquico
            </Button>
            <div className="h-6 w-px bg-border mx-1"></div>
            <div className="flex items-center gap-2 px-2">
              <Switch 
                id="compact-mode" 
                checked={isCompact} 
                onCheckedChange={setIsCompact}
                data-testid="compact-mode-switch"
              />
              <Label htmlFor="compact-mode" className="text-xs font-medium cursor-pointer flex items-center gap-1">
                <Minimize2 className="h-3 w-3" />
                Compacto
              </Label>
            </div>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Saved Maps Dropdown with delete option */}
          <div className="flex items-center gap-1">
            <Select value={currentMapId ? String(currentMapId) : 'full'} onValueChange={(v) => loadSavedMap(v === 'full' ? null : parseInt(v))}>
              <SelectTrigger className="w-[180px] bg-primary/5 border-primary/20" data-testid="saved-maps-select">
                <FolderOpen className="h-4 w-4 mr-2 text-primary" />
                <SelectValue placeholder="Mapas Guardados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">
                  <span className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Mapa Completo
                  </span>
                </SelectItem>
                {savedMaps.map(m => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    <span className="flex items-center gap-2">
                      {m.is_shared ? <Share2 className="h-3 w-3 text-blue-500" /> : null}
                      {m.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Delete button - only visible when a saved map is selected */}
            {currentMapId && canDelete && isCurrentMapOwner && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-destructive hover:bg-destructive/10"
                onClick={() => {
                  const mapName = currentMap?.name || 'este mapa';
                  if (confirm(`¿Eliminar "${mapName}"?`)) {
                    deleteMap(currentMapId);
                  }
                }}
                title="Eliminar mapa guardado"
                data-testid="delete-saved-map-btn"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="h-6 w-px bg-border"></div>

          {/* Focus on App */}
          <Select value={focusAppId} onValueChange={setFocusAppId}>
            <SelectTrigger className="w-[180px]" data-testid="focus-app-select">
              <Target className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Centrar en App" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las Apps</SelectItem>
              {applications.map(app => (
                <SelectItem key={app.id} value={String(app.id)}>
                  {app.code} - {app.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar app..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-[160px]"
              data-testid="search-input"
            />
          </div>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]" data-testid="filter-type">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {types.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {statuses.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterCriticity} onValueChange={setFilterCriticity}>
            <SelectTrigger className="w-[120px]" data-testid="filter-criticity">
              <SelectValue placeholder="Criticidad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {criticities.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterServer} onValueChange={setFilterServer}>
            <SelectTrigger className="w-[160px]" data-testid="filter-server">
              <Server className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Servidor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {servers.map(s => (
                <SelectItem key={s.id} value={String(s.id)}>{s.hostname || s.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
              Limpiar
            </Button>
          )}
          
          <div className="flex items-center gap-2 ml-auto">
            {canCreate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSaveMapModal({ open: true, mode: 'create', data: null })}
                className="gap-2"
                data-testid="save-map-btn"
              >
                <Save className="h-4 w-4" />
                Guardar como Nuevo
              </Button>
            )}
            {currentMapId && canUpdate && isCurrentMapOwner && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSaveMapModal({ open: true, mode: 'update', data: savedMaps.find(m => m.id === currentMapId) })}
                className="gap-2"
                data-testid="update-map-btn"
              >
                <RefreshCw className="h-4 w-4" />
                Actualizar Mapa
              </Button>
            )}
            <Button variant="outline" size="icon" onClick={fetchMapData} title="Recargar">
              <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Map */}
        <Card className="flex-1">
          <CardContent className="p-0 h-full">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-muted-foreground">Cargando mapa...</p>
                </div>
              </div>
            ) : nodes.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Box className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No hay aplicaciones para mostrar</p>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={clearFilters}>
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onNodeDragStop={onNodeDragStop}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseMove={onNodeMouseMove}
                onNodeMouseLeave={onNodeMouseLeave}
                onEdgeClick={onEdgeClick}
                onEdgeMouseEnter={onEdgeMouseEnter}
                onEdgeMouseMove={onEdgeMouseMove}
                onEdgeMouseLeave={onEdgeMouseLeave}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.1}
                maxZoom={2}
                className="bg-slate-50 dark:bg-slate-900/50"
              >
                <Background color="#94a3b8" gap={24} size={1} />
                <Controls className="!bg-background !border !shadow-lg" />
              </ReactFlow>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Tooltip */}
      <Tooltip data={tooltip.data} position={tooltip.position} type={tooltip.type} />
      
      {/* Interface Detail Modal */}
      <Dialog open={interfaceModal.open} onOpenChange={(open) => setInterfaceModal({ open, data: open ? interfaceModal.data : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              {interfaceModal.data?.name || 'Interface'}
            </DialogTitle>
          </DialogHeader>
          
          {interfaceModal.data && (
            <div className="space-y-4">
              {/* Type & Direction */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tipo</p>
                  <p className="font-medium text-cyan-600 dark:text-cyan-400">{interfaceModal.data.interface_type || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Dirección</p>
                  <div className="flex items-center gap-1.5">
                    <InterfaceDirectionIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{interfaceDirection.label}</span>
                  </div>
                </div>
              </div>
              
              {/* Criticity */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Criticidad</p>
                <span className={`inline-flex px-2.5 py-1 rounded-lg text-sm font-semibold ${
                  interfaceModal.data.criticity === 'ALTA' ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' :
                  interfaceModal.data.criticity === 'MEDIA' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                  'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400'
                }`}>
                  {interfaceModal.data.criticity || 'BAJA'}
                </span>
              </div>
              
              {/* Route: Source → Target */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-2">Conexión</p>
                <div className="flex items-center gap-2 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="font-medium">{interfaceModal.data.source_code}</span>
                    <span className="text-muted-foreground text-xs">({interfaceModal.data.source_name})</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${interfaceModal.data.target_code ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                    <span className="font-medium">{interfaceModal.data.target_code || interfaceModal.data.external_system_name}</span>
                    {interfaceModal.data.target_name && (
                      <span className="text-muted-foreground text-xs">({interfaceModal.data.target_name})</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              {interfaceModal.data.notes && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Notas</p>
                  <p className="text-sm text-muted-foreground italic">{interfaceModal.data.notes}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Link to={`/apps/${interfaceModal.data.source_app_id}`} className="flex-1">
                  <Button variant="outline" className="w-full gap-2">
                    <Globe className="h-4 w-4" />
                    Ver App Origen
                  </Button>
                </Link>
                <Link to="/apps/interfaces" className="flex-1">
                  <Button className="w-full gap-2">
                    <Link2 className="h-4 w-4" />
                    Ver Interfaces
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* CSS for highlight/dim effect */}
      <style>{`
        .react-flow__node.dimmed-node {
          opacity: 0.2;
          transition: opacity 0.2s ease;
        }
        .react-flow__node.highlighted-node {
          opacity: 1;
          z-index: 10;
          transition: opacity 0.2s ease;
        }
        .react-flow__edge {
          cursor: pointer;
        }
      `}</style>
      
      {/* Save Map Modal */}
      <Dialog open={saveMapModal.open} onOpenChange={(open) => setSaveMapModal({ ...saveMapModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-primary" />
              {saveMapModal.mode === 'update' ? 'Actualizar Mapa Existente' : 'Guardar Nueva Vista'}
            </DialogTitle>
          </DialogHeader>
          
          <SaveMapForm 
            initialData={saveMapModal.data}
            onSave={(name, description, isShared) => saveMap(name, description, isShared, saveMapModal.mode === 'update' ? saveMapModal.data?.id : null)}
            onCancel={() => setSaveMapModal({ open: false, mode: 'create', data: null })}
            onDelete={saveMapModal.mode === 'update' && canDelete && isCurrentMapOwner ? () => {
              deleteMap(saveMapModal.data?.id);
              setSaveMapModal({ open: false, mode: 'create', data: null });
            } : null}
          />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// Save Map Form Component
const SaveMapForm = ({ initialData, onSave, onCancel, onDelete }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [isShared, setIsShared] = useState(initialData?.is_shared || false);
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="map-name">Nombre del Mapa *</Label>
        <Input 
          id="map-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Ecosistema ERP Financiero"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label htmlFor="map-description">Descripción</Label>
        <Textarea 
          id="map-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción opcional del mapa..."
          className="mt-1"
          rows={3}
        />
      </div>
      
      <div className="flex items-center gap-3">
        <Switch 
          id="map-shared" 
          checked={isShared} 
          onCheckedChange={setIsShared}
        />
        <Label htmlFor="map-shared" className="cursor-pointer flex items-center gap-2">
          <Share2 className="h-4 w-4 text-blue-500" />
          Compartir con otros usuarios
        </Label>
      </div>
      
      <DialogFooter className="flex gap-2 pt-4">
        {onDelete && (
          <Button variant="destructive" size="sm" onClick={onDelete} className="mr-auto">
            <Trash2 className="h-4 w-4 mr-1" />
            Eliminar
          </Button>
        )}
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={() => onSave(name, description, isShared)} disabled={!name.trim()}>
          <Save className="h-4 w-4 mr-1" />
          Guardar
        </Button>
      </DialogFooter>
    </div>
  );
};

// Wrap with ReactFlowProvider to enable useReactFlow hook
const ApplicationMapWithProvider = () => (
  <ReactFlowProvider>
    <ApplicationMap />
  </ReactFlowProvider>
);

export default ApplicationMapWithProvider;
